import { GenericActivityListSort } from 'src/models/genericActivityList';
import { IdoInstanceSearchResult, IdoListViewModel } from 'src/models/idoList';

import { Callback } from 'src/util/query/models/Callback';

import { IdoListCallbackStoreContract } from '../interfaces';
import { Pagination } from '../types';

const FALLBACK_INSTANCE_COLOR = '#f5f5f5';

const DEFAULT_TAB_PAGINATION: Pagination = {
	page: 1,
	rowsNumber: 0,
	rowsPerPage: 20,
	sort: {
		direction: 'ASC',
		property: '_id',
	},
};

export class GetTheIdoListviewModelAndDataQueryCallBack extends Callback<IdoListViewModel> {
	private readonly targetTabId: null | string;

	public constructor(
		private readonly store: IdoListCallbackStoreContract,
		private readonly idoInstanceId: string,
		private readonly searchTerm: string,
		targetTabId: null | string,
		private readonly currentPage: number,
		sort: Array<GenericActivityListSort>,
		private readonly limit: number,
		private readonly queryIdentifier: string
	) {
		super();
		this.targetTabId = targetTabId;
		this.sort = sort.length > 0 ? sort : [{ direction: 'ASC', property: '_id' }];
	}

	private readonly sort: Array<GenericActivityListSort>;

	private createEmptyInstanceResult(): IdoInstanceSearchResult {
		const targetTab =
			this.targetTabId !== null ? this.store.tabs.find((tab) => tab.id === this.targetTabId) : undefined;
		const tabLabel =
			targetTab?.type === 'instance'
				? targetTab.instance?.Name
				: targetTab?.type === 'sharedQuery'
					? targetTab.sharedQuery?.label
					: undefined;
		const tabColor =
			targetTab?.type === 'instance'
				? targetTab.instance?.Color || FALLBACK_INSTANCE_COLOR
				: FALLBACK_INSTANCE_COLOR;

		return {
			instance: tabLabel || 'Leere Ergebnisliste',
			instanceColor: tabColor,
			instanceId: parseInt(this.idoInstanceId) || 0,
			results: [],
			totalCount: 0,
		};
	}

	failureCallbackFunction = (failureResponse?: unknown): void => {
		if (!this.isLatestQuery()) {
			return;
		}

		console.error('[IDO LIST] Fehler beim Laden der Aktenliste:', {
			error: failureResponse,
			instanceId: this.idoInstanceId,
			searchTerm: this.searchTerm,
			timestamp: new Date().toISOString(),
		});

		this.store.setSearchInProgress(false);

		if (this.idoInstanceId === 'all') {
			this.store.setGlobalSearchCounts([]);
			return;
		}

		if (this.targetTabId === null) {
			return;
		}

		const fallbackResult = {
			instance: 'Fehler beim Laden',
			instanceColor: FALLBACK_INSTANCE_COLOR,
			instanceId: parseInt(this.idoInstanceId) || 0,
			results: [],
			totalCount: 0,
		} as unknown as IdoInstanceSearchResult;

		this.store.setTabData({
			data: fallbackResult,
			pagination: {
				page: this.currentPage,
				rowsNumber: 0,
				rowsPerPage: 20,
				sort: this.sort[0] ?? DEFAULT_TAB_PAGINATION.sort,
			},
			searchTerm: this.searchTerm,
			tabId: this.targetTabId,
		});
	};

	private isLatestQuery(): boolean {
		return this.store.isLatestIdoListQueryIdentifier({
			idoInstanceId: this.idoInstanceId,
			queryIdentifier: this.queryIdentifier,
			targetTabId: this.targetTabId,
		});
	}

	successCallbackFunction = (successResponse: IdoListViewModel): void => {
		if (!this.isLatestQuery()) {
			return;
		}

		if (this.idoInstanceId === 'all') {
			this.store.setIdoList({
				idoInstanceId: 'all',
				idoListViewModel: {
					isEmpty: successResponse.results.length === 0,
					isLoading: false,
					results: successResponse.results,
					totalRows: successResponse.results.reduce((sum, result) => sum + result.totalCount, 0),
				} as unknown as IdoInstanceSearchResult,
			});

			this.store.setGlobalSearchCounts(
				successResponse.results.map((result) => ({
					instanceColor: result.instanceColor || FALLBACK_INSTANCE_COLOR,
					instanceId: String(result.instanceId),
					instanceName: result.instance,
					totalCount: result.totalCount,
				}))
			);

			successResponse.results.forEach((result) => {
				this.store.setIdoListResultCount({
					idoInstanceId: String(result.instanceId),
					idoListResultCount: result.totalCount,
				});
			});
		} else {
			const instanceResult = successResponse.results[0];
			if (instanceResult === undefined) {
				const emptyResult = this.createEmptyInstanceResult();

				if (this.targetTabId !== null) {
					const basePagination = this.store.pagination.get(this.idoInstanceId) ?? DEFAULT_TAB_PAGINATION;
					this.store.setTabData({
						data: emptyResult,
						pagination: {
							...basePagination,
							page: this.currentPage,
							rowsNumber: 0,
							rowsPerPage: this.limit,
							sort: this.sort[0] ?? DEFAULT_TAB_PAGINATION.sort,
						},
						resultCount: 0,
						searchTerm: this.searchTerm,
						tabId: this.targetTabId,
					});
				} else {
					this.store.setIdoList({
						idoInstanceId: this.idoInstanceId,
						idoListViewModel: emptyResult,
					});
				}

				this.store.setIdoListResultCount({
					idoInstanceId: this.idoInstanceId,
					idoListResultCount: 0,
				});
				this.store.setSearchInProgress(false);
				return;
			}

			if (this.targetTabId !== null) {
				const targetTab = this.store.tabs.find((tab) => tab.id === this.targetTabId);

				if (targetTab === undefined) {
					this.store.setIdoList({
						idoInstanceId: this.idoInstanceId,
						idoListViewModel: instanceResult,
					});
				} else {
					const basePagination = this.store.pagination.get(this.idoInstanceId) ?? DEFAULT_TAB_PAGINATION;
					const tabPagination = {
						...basePagination,
						page: this.currentPage,
						rowsNumber: instanceResult.totalCount,
						rowsPerPage: this.limit,
						sort: this.sort[0] ?? DEFAULT_TAB_PAGINATION.sort,
					};

					if (!instanceResult.instanceColor) {
						instanceResult.instanceColor = FALLBACK_INSTANCE_COLOR;
					}

					this.store.setTabData({
						data: instanceResult,
						pagination: tabPagination,
						resultCount: instanceResult.totalCount,
						searchTerm: this.searchTerm,
						tabId: this.targetTabId,
					});
				}
			} else {
				this.store.setIdoList({
					idoInstanceId: this.idoInstanceId,
					idoListViewModel: instanceResult,
				});
			}

			this.store.setIdoListResultCount({
				idoInstanceId: this.idoInstanceId,
				idoListResultCount: instanceResult.totalCount,
			});
		}

		this.store.setSearchInProgress(false);
	};
}
